// ---------------------------------------------------------------------------
// InputsLegend — card de legenda compartilhado pelas duas views de inputs.
// ---------------------------------------------------------------------------
// Renderiza a faixa horizontal com swatches + labels que comunicam o
// vocabulario canonico de estados de input. Usado em:
//   - ``OverviewV3`` (visao geral / heatmap input x mes);
//   - ``InputsV3`` (visao detalhada / cards InputBoxV3).
//
// Fonte unica das cores: ``INPUT_STATE_BACKGROUNDS`` em
// ``services/inputsV3StateRules.js`` — mesma paleta que pinta as cells
// do overview e o pip unico do InputBoxV3 (via ``inputSinglePipState``).
//
// ``not_sent`` abre a legenda, seguido por ``in_progress`` (verde claro:
// "Aguardando para avancar") e ``delivered_ok`` (verde escuro: "OK"),
// para desambiguar inputs 100% concluidos daqueles que ainda aguardam
// dependencias.
// ---------------------------------------------------------------------------

import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import Tooltip from "@material-ui/core/Tooltip";
import {
  PersonOutline,
  Repeat,
  FlashOn,
  Event,
  HelpOutline,
  TrendingFlat,
} from "@material-ui/icons";

import { grayColor } from "../../../../assets/jss/material-dashboard-pro-react";
import {
  INPUT_STATE_BACKGROUNDS,
  IN_PROGRESS_LEGEND_BACKGROUND,
} from "../../../../services/inputsV3StateRules";
import { pipBackgroundForInFlight } from "../StagePips/StagePips";

const HEADER_BG = "#fafafa";
const BORDER_COLOR = grayColor[5];

// Variantes de empacotamento:
//   - ``bordered`` (default): com borda e ``marginBottom`` — usado
//     dentro de outro card (OverviewV3 dentro do ``CardBody``);
//   - ``flat``: sem borda e com ``marginTop`` igual ao ``marginTop``
//     dos cards-irmaos do stack (8px). Mantido para reuso eventual.
//   - ``tooltip``: card compacto para popover/tooltip da barra de
//     controles da visao detalhada.
const useStyles = makeStyles(() => ({
  legendBordered: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    flexWrap: "wrap",
    padding: "8px 10px",
    marginBottom: "10px",
    background: HEADER_BG,
    border: `1px solid ${BORDER_COLOR}`,
    borderRadius: "6px",
  },
  legendFlat: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    flexWrap: "wrap",
    padding: "8px 10px",
    marginTop: "8px",
    marginBottom: "0",
    background: HEADER_BG,
    borderRadius: "6px",
  },
  legendTooltip: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "8px",
    padding: "10px 12px",
    width: "max-content",
    maxWidth: "none",
    background: "#ffffff",
    border: `1px solid ${BORDER_COLOR}`,
    borderRadius: "6px",
    boxShadow: "0 8px 22px rgba(0,0,0,0.16)",
  },
  // Variante "popup vertical" (rev. 2026-05-26): duas colunas
  // empilhadas — esquerda com swatches de estado, divisor vertical,
  // direita com icones de marker. Cada item ocupa uma linha propria
  // dentro da sua coluna. Substitui o layout horizontal antigo que
  // ficava largo demais no popover.
  legendTooltipTwoCols: {
    display: "flex",
    flexDirection: "row",
    alignItems: "stretch",
    gap: "14px",
    padding: "12px 14px",
    width: "max-content",
    maxWidth: "none",
    background: "#ffffff",
    borderRadius: "6px",
    boxShadow: "0 8px 22px rgba(0,0,0,0.16)",
    marginTop: 2,
  },
  tooltipColumn: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    minWidth: 0,
  },
  tooltipColumnDivider: {
    width: "1px",
    background: BORDER_COLOR,
    flexShrink: 0,
    alignSelf: "stretch",
  },
  // ``pill`` da legenda — disparador visual do popover. Mantido aqui
  // como exporte do modulo para que as duas views (visao detalhada e
  // overview) compartilhem o MESMO componente e estilo, evitando
  // copia/cola.
  pill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    padding: "3px 8px 3px 5px",
    borderRadius: "999px",
    border: `1px solid ${BORDER_COLOR}`,
    background: "#fafafa",
    color: grayColor[1],
    fontSize: "0.72rem",
    fontWeight: 500,
    lineHeight: 1.2,
    cursor: "help",
    whiteSpace: "nowrap",
    userSelect: "none",
  },
  pillIcon: {
    width: 14,
    height: 14,
  },
  pillTooltipRoot: {
    background: "transparent",
    padding: 0,
    margin: 0,
    maxWidth: "none",
  },
  pillTooltipArrow: {
    color: "#ffffff",
    "&::before": {
      border: `1px solid ${BORDER_COLOR}`,
      backgroundColor: "#ffffff",
    },
  },
  title: {
    fontSize: "10px",
    color: grayColor[1],
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    marginRight: "4px",
  },
  tooltipTitle: {
    width: "100%",
    fontSize: "10px",
    color: grayColor[1],
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  },
  tooltipRow: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexWrap: "nowrap",
    marginTop: "4px",
  },
  item: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
  },
  swatch: {
    width: "14px",
    height: "12px",
    borderRadius: "3px",
    flexShrink: 0,
  },
  // Pacote 1.5: swatch animado do MODIFICADOR "processando". Mesmo
  // perfil de ripple usado em ``StagePips.inFlight`` (gradiente
  // horizontal ``base -> luz -> base`` deslizando via
  // ``backgroundPosition``). A cor base e' delivered_ok (verde
  // escuro) — caso mais comum onde o consultor encontra o overlay
  // ("OK + cruzamentos rodando").
  swatchRipple: {
    width: "14px",
    height: "12px",
    borderRadius: "3px",
    flexShrink: 0,
    backgroundSize: "200% 100%",
    animation: "$legendRippleWave 1400ms ease-in-out infinite",
  },
  "@keyframes legendRippleWave": {
    "0%": { backgroundPosition: "200% 0" },
    "100%": { backgroundPosition: "-200% 0" },
  },
  label: {
    fontSize: "11.5px",
    color: grayColor[1],
    whiteSpace: "nowrap",
  },
  markerDivider: {
    width: "1px",
    height: "18px",
    background: grayColor[5],
    margin: "0 2px",
  },
  markerIcon: {
    width: "15px",
    height: "15px",
    color: grayColor[1],
  },
}));

// Items canonicos da legenda. ``bucket`` pega a cor em
// ``INPUT_STATE_BACKGROUNDS``; ``labelKey`` aponta para a entrada i18n
// em ``modeling_inputs_v3.header.state_*`` (reuso das mesmas strings
// que ja aparecem no header do drawer / chips de progresso — fonte
// unica de copy por bucket).
//
// Excecao visual: ``in_progress`` agora cobre DOIS estados runtime —
// ``in_progress`` (verde claro, parcial sem aviso) e ``in_progress_attention``
// (amarelo claro, parcial com aviso). A caixa da legenda passa a
// renderizar com gradient diagonal (``IN_PROGRESS_LEGEND_BACKGROUND``)
// para representar essa dualidade num so swatch — o label permanece
// "Aguardando para avancar".
const LEGEND_ITEMS = Object.freeze([
  ["not_sent", "modeling_inputs_v3.header.state_not_sent"],
  ["in_progress", "modeling_inputs_v3.header.state_in_progress"],
  ["delivered_ok", "modeling_inputs_v3.header.state_delivered_ok"],
  ["attention", "modeling_inputs_v3.header.state_attention"],
  ["pending_decision", "modeling_inputs_v3.header.state_pending_decision"],
  ["critical", "modeling_inputs_v3.header.state_critical"],
  // ``system_error`` (D-35): falha tecnica genuina do pipeline.
  // Bucket distinto de ``critical`` (cliente precisa agir) — preto
  // diferencia visualmente. Cor vem de
  // ``INPUT_STATE_BACKGROUNDS.system_error`` em ``inputsV3StateRules``.
  ["system_error", "modeling_inputs_v3.header.state_processing_error"],
]);

const _swatchBg = (bucket) =>
  bucket === "in_progress"
    ? IN_PROGRESS_LEGEND_BACKGROUND
    : INPUT_STATE_BACKGROUNDS[bucket];

// Pacote 1.5: cor base do swatch animado do MODIFICADOR. Usamos a
// mesma cor de ``delivered_ok`` — caso mais comum onde o consultor
// ve o overlay (input ja conferido + cruzamentos em fila).
const RIPPLE_DEMO_BG = pipBackgroundForInFlight(
  INPUT_STATE_BACKGROUNDS.delivered_ok,
);

const MARKER_ITEMS = Object.freeze([
  ["mine", "modeling_inputs_v3.legend.marker_mine", PersonOutline],
  ["reuse_auto", "modeling_inputs_v3.legend.marker_reuse_auto", Repeat],
  ["singleton", "modeling_inputs_v3.legend.marker_singleton", FlashOn],
  [
    "carry_forward",
    "modeling_inputs_v3.legend.marker_carry_forward",
    TrendingFlat,
  ],
  ["yearly", "modeling_inputs_v3.legend.marker_yearly", Event],
  ["optional", "modeling_inputs_v3.legend.marker_optional", HelpOutline],
]);

const InputsLegend = ({ variant }) => {
  const c = useStyles();
  const { t } = useTranslation();
  const wrapperClass =
    variant === "tooltip"
      ? c.legendTooltip
      : variant === "flat"
        ? c.legendFlat
        : c.legendBordered;

  // Pacote 1.5: nodo do "modificador processando" — reutilizado nos
  // dois variants. NAO e' um bucket de estado (nao tem cor propria);
  // e' um overlay que pode acontecer SOBRE qualquer cor base quando
  // ha cruzamento rodando para o input. Por isso vem apos um divider
  // para nao ser confundido com um estado novo.
  const rippleModifierNode = (
    <span className={c.item}>
      <span
        className={c.swatchRipple}
        style={{ backgroundImage: RIPPLE_DEMO_BG }}
        title={t("modeling_inputs_v3.legend.processing_modifier_title")}
      />
      <span className={c.label}>
        {t("modeling_inputs_v3.legend.processing_modifier_label")}
      </span>
    </span>
  );

  if (variant === "tooltip") {
    // Rev. 2026-05-26: layout vertical em duas colunas. Esquerda =
    // bucket de estados (swatches) + modificador "processando" no
    // fim do bloco; direita = markers (icones de descritor). Divisor
    // vertical entre os dois. Cada item em sua propria linha dentro
    // da coluna.
    return (
      <div className={c.legendTooltipTwoCols}>
        <div className={c.tooltipColumn}>
          <span className={c.tooltipTitle}>
            {t("modeling_inputs_v3.legend.tooltip_title")}
          </span>
          {LEGEND_ITEMS.map(([bucket, labelKey]) => (
            <span key={bucket} className={c.item}>
              <span
                className={c.swatch}
                style={{ background: _swatchBg(bucket) }}
              />
              <span className={c.label}>{t(labelKey)}</span>
            </span>
          ))}
          {rippleModifierNode}
        </div>
        <span className={c.tooltipColumnDivider} aria-hidden="true" />
        <div className={c.tooltipColumn}>
          <span className={c.tooltipTitle}>
            {t("modeling_inputs_v3.legend.tooltip_markers_title")}
          </span>
          {MARKER_ITEMS.map(([key, labelKey, Icon]) => (
            <span key={key} className={c.item}>
              <Icon className={c.markerIcon} />
              <span className={c.label}>{t(labelKey)}</span>
            </span>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={wrapperClass}>
      <span className={c.title}>{t("modeling_inputs_v3.legend.title")}</span>
      {LEGEND_ITEMS.map(([bucket, labelKey]) => (
        <span key={bucket} className={c.item}>
          <span
            className={c.swatch}
            style={{ background: _swatchBg(bucket) }}
          />
          <span className={c.label}>{t(labelKey)}</span>
        </span>
      ))}
      <span className={c.markerDivider} aria-hidden="true" />
      {rippleModifierNode}
      <span className={c.markerDivider} aria-hidden="true" />
      {MARKER_ITEMS.map(([key, labelKey, Icon]) => (
        <span key={key} className={c.item}>
          <Icon className={c.markerIcon} />
          <span className={c.label}>{t(labelKey)}</span>
        </span>
      ))}
    </div>
  );
};

InputsLegend.propTypes = {
  variant: PropTypes.oneOf(["bordered", "flat", "tooltip"]),
};

InputsLegend.defaultProps = {
  variant: "bordered",
};

// Pill disparador do popover de legenda. Compartilhado por
// ``ModelingProgressBandV3`` (visao detalhada — pill ao lado da barra
// de avanco) e ``OverviewV3`` (linha de filtros da visao geral).
// Centraliza estilo (mesmo pill em ambos os locais) e mantem o
// popover sincronizado com a forma vertical em 2 colunas.
export const InputsLegendPill = ({ placement = "bottom-end" }) => {
  const c = useStyles();
  const { t } = useTranslation();
  return (
    <Tooltip
      title={<InputsLegend variant="tooltip" />}
      arrow
      interactive
      placement={placement}
      enterDelay={250}
      leaveDelay={80}
      classes={{
        tooltip: c.pillTooltipRoot,
        arrow: c.pillTooltipArrow,
      }}
    >
      <span className={c.pill}>
        <HelpOutline className={c.pillIcon} />
        {t("inputs_v3.controls.legend", "legenda")}
      </span>
    </Tooltip>
  );
};

InputsLegendPill.propTypes = {
  placement: PropTypes.string,
};

export default InputsLegend;
